import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "../../hooks/useApp";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Grid,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import FancyPaper from "../../components/FancyPaper";
import FancySearch from "../../components/utils/FancySearch";
import FancyTablePagination from "../../components/utils/FancyTablePagination";
import WeekFilter from "../../components/utils/credit_view/WeekFilter";
import { getWeekNumber } from "../../utils/date-utils";
import {
  getWeeklyPayrollDetailReport,
  getMonthlyPlanillaReport,
  getWeeklyPayrollExcelReport,
  getMonthlyPlanillaExcelReport,
  WeeklyPayrollDetailReport,
  MonthlyPlanillaReport,
} from "../../services/advances/advancesService";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

const formatCurrency = (value: number | null | undefined) => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "-";
};

const ManagementAdvanceReports = () => {
  const { setLoading, setErrorMsg, setSuccessMsg } = useApp();

  // Table 1 state
  const [preFilter1, setPreFilter1] = useState("");
  const [appliedFilter1, setAppliedFilter1] = useState("");
  const [page1, setPage1] = useState(0);
  const [dataTable1, setDataTable1] = useState<WeeklyPayrollDetailReport["details"]>([]);
  const [totalElements1, setTotalElements1] = useState(0);
  const [totalPages1, setTotalPages1] = useState(1);

  // Table 2 state
  const [preFilter2, setPreFilter2] = useState("");
  const [appliedFilter2, setAppliedFilter2] = useState("");
  const [page2, setPage2] = useState(0);
  const [dataTable2, setDataTable2] = useState<MonthlyPlanillaReport["persons"]>([]);
  const [weekHeaders, setWeekHeaders] = useState<string[]>([]);
  const [selectedMonthDate, setSelectedMonthDate] = useState<Dayjs>(dayjs());
  const [totalElements2, setTotalElements2] = useState(0);
  const [totalPages2, setTotalPages2] = useState(1);

  // WeekFilter state — inicializar a la semana actual para evitar doble fetch en mount
  const [filteredWeek, setFilteredWeek] = useState<number>(() => getWeekNumber(dayjs()));
  const [dateRange, setDateRange] = useState<string>("");
  const [year, setYear] = useState<number>(new Date().getFullYear());

  // Debounce: cuando el usuario tipea en el filtro, esperar antes de disparar el fetch
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedFilter1(preFilter1);
      setPage1(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [preFilter1]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedFilter2(preFilter2);
      setPage2(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [preFilter2]);

  const selectedMonth = useMemo(() => ({
    year: selectedMonthDate.year(),
    month: selectedMonthDate.month() + 1, // 1-indexed for the API
    label: selectedMonthDate.format("MMMM YYYY"),
  }), [selectedMonthDate]);

  const fetchWeeklyReport = useCallback(async () => {
    try {
      const result = await getWeeklyPayrollDetailReport(
        year,
        filteredWeek,
        PAGE_SIZE,
        page1,
        appliedFilter1 || undefined
      );
      setDataTable1(result.details ?? []);
      setTotalElements1(result.totalElements ?? 0);
      setTotalPages1(result.totalPages || 1);
    } catch (error) {
      setDataTable1([]);
      setTotalElements1(0);
      setTotalPages1(1);
    }
  }, [year, filteredWeek, page1, appliedFilter1]);

  const fetchMonthlyReport = useCallback(async () => {
    try {
      const result = await getMonthlyPlanillaReport(
        selectedMonth.year,
        selectedMonth.month,
        PAGE_SIZE,
        page2,
        appliedFilter2 || undefined
      );
      setDataTable2(result.persons ?? []);
      setWeekHeaders(result.weekHeaders ?? []);
      setTotalElements2(result.totalElements ?? 0);
      setTotalPages2(result.totalPages || 1);
    } catch (error) {
      setDataTable2([]);
      setWeekHeaders([]);
      setTotalElements2(0);
      setTotalPages2(1);
    }
  }, [selectedMonth.year, selectedMonth.month, page2, appliedFilter2]);

  useEffect(() => {
    fetchWeeklyReport();
  }, [fetchWeeklyReport]);

  useEffect(() => {
    fetchMonthlyReport();
  }, [fetchMonthlyReport]);

  const handleExportTable1 = async () => {
    setLoading && setLoading(true);
    try {
      await getWeeklyPayrollExcelReport(year, filteredWeek, appliedFilter1 || undefined);
      setSuccessMsg && setSuccessMsg("Informe semanal descargado correctamente");
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
    } finally {
      setLoading && setLoading(false);
    }
  };

  const handleExportTable2 = async () => {
    setLoading && setLoading(true);
    try {
      await getMonthlyPlanillaExcelReport(selectedMonth.year, selectedMonth.month, appliedFilter2 || undefined);
      setSuccessMsg && setSuccessMsg("Informe de planillas descargado correctamente");
    } catch (error: any) {
      setErrorMsg && setErrorMsg(error.message);
    } finally {
      setLoading && setLoading(false);
    }
  };

  const handleApplyFilter1 = () => {
    setAppliedFilter1(preFilter1);
    setPage1(0);
  };

  const handleApplyFilter2 = () => {
    setAppliedFilter2(preFilter2);
    setPage2(0);
  };

  return (
    <>
      <FancyPaper
        pagetitle="Informes"
        prevpage="Avances"
        linkprevpage="/dashboard/advances"
      >
        {/* Header: WeekFilter + Export + Search */}
        <Grid container spacing={8} sx={{ pb: 8 }}>
          <Grid item md={12} sm={12} xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
            >
              <WeekFilter
                onWeekSelect={(week, _startDay) => {
                  if (week !== null && week !== filteredWeek) {
                    setFilteredWeek(week);
                    setPage1(0);
                  }
                }}
                dateRange={dateRange}
                setDateRange={setDateRange}
                year={year}
                setYear={(newYear) => {
                  if (newYear !== year) {
                    setYear(newYear);
                    setPage1(0);
                  }
                }}
              />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={2}
                sx={{ flex: "1 1 auto" }}
              >
                <Button variant="contained" onClick={handleExportTable1}>
                  Exportar
                </Button>
                <Box sx={{ flex: "1 1 auto", maxWidth: "550px", minWidth: "550px" }}>
                  <FancySearch
                    preFilter={preFilter1}
                    setPreFilter={setPreFilter1}
                    handleFetchData={() => setPreFilter1("")}
                    handleApplyFilter={handleApplyFilter1 as unknown as () => {}}
                    showOnlySearch={true}
                  />
                </Box>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {/* Table 1: Informe semanal */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Informe semanal
          </Typography>
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="Tabla informe semanal">
            <TableHead>
              <TableRow>
                <TableCell align="center">Id</TableCell>
                <TableCell align="center">Colaborador</TableCell>
                <TableCell align="center">N Cuenta Banco</TableCell>
                <TableCell align="center">Banco</TableCell>
                <TableCell align="center">Sub Total</TableCell>
                <TableCell align="center">Deducción CCSS</TableCell>
                <TableCell align="center">Crédito/Débito</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Pasajes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataTable1.length > 0 ? (
                dataTable1.map((row, idx) => (
                  <TableRow key={`${row.personId}-${idx}`}>
                    <TableCell align="center">{row.personId}</TableCell>
                    <TableCell align="center">{row.name}</TableCell>
                    <TableCell align="center">{row.ibanAccount}</TableCell>
                    <TableCell align="center">{row.bankName}</TableCell>
                    <TableCell align="center">{formatCurrency(row.grossSalary)}</TableCell>
                    <TableCell align="center">{formatCurrency(row.ccssAmount)}</TableCell>
                    <TableCell align="center">{formatCurrency(row.creditDebitAmount)}</TableCell>
                    <TableCell align="center">{formatCurrency(row.netSalary)}</TableCell>
                    <TableCell align="center">{formatCurrency(row.transportationPaymentAmount)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9}>No existen registros</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <FancyTablePagination
          count={dataTable1.length}
          rowsPerPage={PAGE_SIZE}
          page={page1}
          onPageChange={(_e: unknown, newPage: number) => setPage1(newPage - 1)}
          totalElements={totalElements1}
          totalPages={totalPages1}
        />

        {/* Table 2 Header: Title + Month Selector + Search + Export */}
        <Grid container spacing={8} sx={{ mt: 4, pb: 4 }}>
          <Grid item md={12} sm={12} xs={12}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Informe de planillas
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {selectedMonth.label.charAt(0).toUpperCase() + selectedMonth.label.slice(1)}
                </Typography>
              </Box>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Mes"
                  views={["month", "year"]}
                  value={selectedMonthDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setSelectedMonthDate(newValue);
                      setPage2(0);
                    }
                  }}
                  slotProps={{ textField: { size: "small" } }}
                />
              </LocalizationProvider>
              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2} sx={{ flex: "1 1 auto" }}>
                <Button variant="contained" onClick={handleExportTable2}>
                  Exportar
                </Button>
                <Box sx={{ flex: "1 1 auto", maxWidth: "550px" }}>
                  <FancySearch
                    preFilter={preFilter2}
                    setPreFilter={setPreFilter2}
                    handleFetchData={() => setPreFilter2("")}
                    handleApplyFilter={handleApplyFilter2 as unknown as () => {}}
                    showOnlySearch={true}
                  />
                </Box>

              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {/* Table 2: Informe de planillas - headers computed dynamically from selected month */}
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="Tabla informe de planillas">
            <TableHead>
              <TableRow>
                <TableCell align="center">Id</TableCell>
                <TableCell align="center">Colaborador</TableCell>
                {weekHeaders.map((header, idx) => (
                  <TableCell key={idx} align="center">
                    {header}
                  </TableCell>
                ))}
                <TableCell align="center">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataTable2.length > 0 ? (
                dataTable2.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell align="center">{row.id}</TableCell>
                    <TableCell align="center">{row.name}</TableCell>
                    {weekHeaders.map((_, idx) => {
                      const amount = row.weeklyAmounts[idx];
                      return (
                        <TableCell key={idx} align="center">
                          {amount != null ? formatCurrency(amount) : "-"}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">{formatCurrency(row.total)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={weekHeaders.length + 3}>No existen registros</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <FancyTablePagination
          count={dataTable2.length}
          rowsPerPage={PAGE_SIZE}
          page={page2}
          onPageChange={(_e: unknown, newPage: number) => setPage2(newPage - 1)}
          totalElements={totalElements2}
          totalPages={totalPages2}
        />
      </FancyPaper>
    </>
  );
};

export default ManagementAdvanceReports;
